import { cva, cx } from "@nju/css";
import { Icon, IconName } from "@nju/icons";
import { Markdown } from "../Markdown";
import { Tooltip } from "../Tooltip";

interface IIconListElementCommonProps {
  iconName: IconName;
  testId?: string;
  tooltipContent?: string;
  darkTooltipIcon?: boolean;
  alternativeTooltipContent?: string;
  iconSize: "small" | "medium" | "big";
  iconAlign?: "start" | "center";
  mode: "markdown" | "raw";
  borderStyle?: "dashed";
  tooltipIconCentered?: boolean;
  smallPadding?: boolean;
}

interface IIconListElementRawProps extends IIconListElementCommonProps {
  mode: "raw";
  text: string | JSX.Element;
}

interface IIconListElementMarkdownProps extends IIconListElementCommonProps {
  mode: "markdown";
  text: string;
}

type IIconListElementProps =
  | IIconListElementRawProps
  | IIconListElementMarkdownProps;

const icon = cva([], {
  variants: {
    iconSize: {
      small: ["w-3", "h-3", "from-tablet:w-4", "from-tablet:h-4"],
      medium: ["w-4", "h-5", "from-tablet:w-5", "from-tablet:h-5"],
      big: ["w-6", "h-6", "from-tablet:w-7", "from-tablet:h-7"],
    },
  },
});

export function IconListElement({
  iconName,
  text,
  iconSize,
  borderStyle,
  tooltipContent,
  tooltipIconCentered,
  darkTooltipIcon = false,
  iconAlign = "center",
  mode,
  testId,
  smallPadding = false,
}: IIconListElementProps) {
  return (
    <div
      className={cx("flex py-2", {
        "from-tablet:items-start": iconAlign === "start",
        "from-tablet:items-center": iconAlign === "center",
        "border-dashed border-cerulean border-1 rounded-panel px-3":
          borderStyle === "dashed",
      })}
      data-cy={testId}
    >
      <Icon className={icon({ iconSize })} name={iconName} tone="accent" />
      <div
        className={cx("flex-1 flex items-baseline justify-between", {
          "pl-5": !smallPadding,
          "pl-2": smallPadding,
        })}
      >
        {mode === "markdown" && (
          <Markdown
            className="prose prose-img:m-0 prose-h2:mt-[1em] text-size-14 from-tablet:text-size-16"
            content={text}
          />
        )}
        {mode === "raw" && (
          <span className="prose prose-img:m-0 prose-h2:mt-[1em] text-size-14 from-tablet:text-size-16">
            {text}
          </span>
        )}
        {tooltipContent && (
          <div
            className={cx("items-center inline-flex ml-2", {
              "self-center": tooltipIconCentered,
            })}
          >
            <Tooltip
              content={tooltipContent}
              tooltipBgColor={darkTooltipIcon ? "!bg-daintree" : "!bg-geyser"}
              tooltipTextColor={
                darkTooltipIcon ? "!text-white" : "!text-daintree"
              }
            >
              <Icon
                className={cx("relative", {
                  "top-[3px]": !tooltipIconCentered,
                })}
                height={16}
                name={darkTooltipIcon ? "circle-info" : "info-circle"}
                width={16}
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}
